// Single source of truth for the admin console's URL scheme. Both the console
// shell (owns the section) and the drilled-in panels import from here so the
// read and write sides can never disagree on format.
//
// URL shape:
//   /financials
//   /ai
//   /users?user=<id>
//   /support?ticket=<ticketId>
//   /support?user=<id>, /engagement?user=<id>, /ai-feedback?user=<id>
//   /institutions?institution=<id>
//   /ambassadors?ambassador=<id>

const DEFAULT_TAB = 'Overview';

// Canonical tab labels ⇄ URL-safe slugs. Labels with spaces get hyphenated
// slugs.
export const TAB_TO_SLUG = {
  Loop: 'loop',
  Friction: 'friction',
  Overview: 'overview',
  Product: 'product',
  Users: 'users',
  Engagement: 'engagement',
  Support: 'support',
  'AI Feedback': 'ai-feedback',
  // Matches the institution portal section path (`/ai`).
  'Coffee Chats AI': 'ai',
  'Avatar Access': 'avatar-access',
  Institutions: 'institutions',
  'Audit Log': 'audit-log',
  Financials: 'financials',
  Ambassadors: 'ambassadors',
  'Global Alumni Network': 'global-alumni-network',
};

// Tabs that open a user drill-in in place, so Back returns to that tab's list
// instead of the Users tab. A `?user=` on any other path forces Users.
const USER_DRILL_TABS = ['Support', 'Engagement', 'AI Feedback'];

const SLUG_TO_TAB = Object.fromEntries(
  Object.entries(TAB_TO_SLUG).map(([tab, slug]) => { return [slug, tab]; }),
);

// Older bookmarks / shared links used the hyphenated label slug.
const LEGACY_SLUG_TO_TAB = {
  'coffee-chats-ai': 'Coffee Chats AI',
};

const slugFromPathname = (pathname = '') => {
  return String(pathname).replace(/^\/+|\/+$/g, '');
};

/**
 * The signed-out URL.
 *
 * Deliberately NOT in TAB_TO_SLUG: it is not a section of the console, it is
 * the absence of a session. Keeping it out means readInitialAdminState still
 * falls through to the default tab, so a user who signs in from /login lands on
 * Overview rather than on a phantom "Login" tab.
 *
 * The console used to canonicalize to /overview regardless of auth state, so a
 * signed-out visitor saw a URL claiming a section they could not see.
 */
export const LOGIN_PATH = '/login';

export function isKnownAdminPath(pathname) {
  const slug = slugFromPathname(pathname);
  // `login` is admitted so a post-OAuth returnPath of /login sanitizes rather
  // than being rejected as unknown; it maps to no tab, so the caller's default
  // still applies.
  if (slug === 'login') return true;
  return slug === '' || Boolean(SLUG_TO_TAB[slug] || LEGACY_SLUG_TO_TAB[slug]);
}

/**
 * Reconcile the browser URL into the console's initial state on mount. The
 * pathname owns the active section; query params are reserved for drill-ins.
 * Legacy `?tab=<slug>` links remain readable and are canonicalized after load.
 *
 * Rules:
 *   - A `user` id on a tab that hosts its own user drill-in (Support,
 *     Engagement, AI Feedback) stays on that tab. A Support link may carry a
 *     `ticket` too: the user was opened from that ticket.
 *   - Otherwise a `user` id forces the Users tab so a shared `?user=` link
 *     opens straight into that account.
 *   - Otherwise a `ticket` id forces the Support tab and opens that ticket.
 *   - Otherwise an `institution` id forces the Institutions tab so a shared
 *     `?institution=` link opens straight into that school.
 *   - Otherwise an `ambassador` id forces the Ambassadors tab so a shared
 *     `?ambassador=` link opens straight into that ambassador's performance.
 *   - Otherwise map the pathname slug back to its label via SLUG_TO_TAB.
 *   - At `/`, honor a legacy `?tab=<slug>` link.
 *   - Unknown or absent slugs fall back to Overview.
 *
 * @returns {{ tab: string, userId: string|null, ticketId: string|null, institutionId: string|null, ambassadorId: string|null }}
 */
export function readInitialAdminState() {
  const empty = {
    userId: null, ticketId: null, institutionId: null, ambassadorId: null,
  };
  if (typeof window === 'undefined' || !window.location) {
    return { ...empty, tab: DEFAULT_TAB };
  }
  const params = new URLSearchParams(window.location.search || '');
  const pathSlug = slugFromPathname(window.location.pathname);
  const pathTab = SLUG_TO_TAB[pathSlug] || LEGACY_SLUG_TO_TAB[pathSlug] || null;

  const userId = params.get('user');
  const ticketId = params.get('ticket');
  if (userId && USER_DRILL_TABS.includes(pathTab)) {
    return {
      ...empty, tab: pathTab, userId, ticketId: pathTab === 'Support' ? ticketId || null : null,
    };
  }
  if (userId) {
    return { ...empty, tab: 'Users', userId };
  }

  if (ticketId) {
    return { ...empty, tab: 'Support', ticketId };
  }

  const institutionId = params.get('institution');
  if (institutionId) {
    return { ...empty, tab: 'Institutions', institutionId };
  }

  const ambassadorId = params.get('ambassador');
  if (ambassadorId) {
    return { ...empty, tab: 'Ambassadors', ambassadorId };
  }

  const tab = pathTab
    || (pathSlug ? null : SLUG_TO_TAB[params.get('tab')] || LEGACY_SLUG_TO_TAB[params.get('tab')]);
  return { ...empty, tab: tab || DEFAULT_TAB };
}

/**
 * Build a same-origin console URL for a section and optional drill-in.
 *
 * @param {{ tab: string, userId?: string|null, ticketId?: string|null, institutionId?: string|null, ambassadorId?: string|null }} state
 */
export function buildAdminUrl({
  tab, userId, ticketId, institutionId, ambassadorId,
}) {
  let effectiveTab = tab;
  if (userId) {
    effectiveTab = USER_DRILL_TABS.includes(tab) ? tab : 'Users';
  } else if (ticketId) {
    effectiveTab = 'Support';
  } else if (institutionId) {
    effectiveTab = 'Institutions';
  } else if (ambassadorId) {
    effectiveTab = 'Ambassadors';
  }
  const slug = TAB_TO_SLUG[effectiveTab] || TAB_TO_SLUG[DEFAULT_TAB];
  const params = new URLSearchParams();
  if (ticketId && effectiveTab === 'Support') {
    params.set('ticket', ticketId);
  }
  if (userId) {
    params.set('user', userId);
  }
  if (institutionId) {
    params.set('institution', institutionId);
  }
  if (ambassadorId) {
    params.set('ambassador', ambassadorId);
  }
  const search = params.toString();
  return `/${slug}${search ? `?${search}` : ''}`;
}

/**
 * Navigate without remounting the console. Regular navigation pushes a browser
 * history entry; canonicalization can replace the current entry.
 */
export function writeAdminUrl(state, { replace = false } = {}) {
  if (typeof window === 'undefined' || !window.history) { return; }
  const target = buildAdminUrl(state);
  const current = `${window.location?.pathname || ''}${window.location?.search || ''}`;
  if (!replace && target === current) { return; }
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method](window.history.state, '', target);
}

export function canonicalizeAdminUrl() {
  if (typeof window === 'undefined' || !window.location) { return; }
  const state = readInitialAdminState();
  const canonical = buildAdminUrl(state);
  const current = `${window.location.pathname}${window.location.search || ''}`;
  if (canonical !== current) {
    writeAdminUrl(state, { replace: true });
  }
}

/**
 * Click handler for an in-console link to another tab or drill-in. A plain
 * click moves there without a reload: pushState, then a popstate so App and
 * the target panel re-read the URL, the same path Back takes. A modified click
 * (new tab, new window) keeps the browser default, so give the link a real
 * `href={buildAdminUrl(state)}`.
 */
export function followAdminLink(e, state) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) { return; }
  e.preventDefault();
  writeAdminUrl(state);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
